import React, { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import { forEach, map, transform } from 'lodash';
import { translate } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import { afterChartReDrawStringData, chartLegendItemClick, setColor, calculateDynamicChartHeight } from '../../helpers';
import { renderToStaticMarkup } from 'react-dom/server';
import { TrendTooltip } from 'sharedComponents/ChartComponents/Trend';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { defaultBoostThreshold } from 'helpers/constants';
import { getMeasurementUnitSymbol } from 'helpers/unitHelper';
import {
    Alignment,
    CHART_CONDITION_TREND_KPI_TYPE,
    defaultOverallVibrationSignal,
    speedKPI,
    statusBarColors,
    RETRAINING_STATUS,
} from './constants';
import { LABEL_EDGE_MARGIN, MIN_LABEL_MAX_WIDTH, CLOSE_LABEL_PIXEL_THRESHOLD, PLOTLINE_DEFAULT_LABEL_Y, PLOTLINE_STACKED_LABEL_Y } from '../DriveAnomalyDetection/CoolingAnomalyChart/constants';
import { chartHeightConstants } from '../../constants';
import { getXAxisLabelDateFormatForDriveAnomaly } from '../DriveAnomalyDetection/helpers';

const PRINT_FONT_FAMILY = 'ABBvoice-Regular';

const updateOppositeAxis = (axis, idx) => {
    axis.update({ opposite: idx % 2 !== 0 }, false);
};

const getVisibleSignalAxes = (chart) =>
    chart?.yAxis?.filter(axis => axis?.options?.isSignalYAxis && axis?.visible);

const collectSeriesVisibility = (series, acc) => {
    if (series?.options?.showInLegend !== false && series?.options?.id) {
        acc[series?.options?.id] = series?.visible;
    }
};

const collectYAxisVisibility = (axis, visibilityAcc, oppositeAcc) => {
    if (axis?.options?.id) {
        visibilityAcc[axis?.options?.id] = axis?.visible;
        oppositeAcc[axis?.options?.id] = axis?.opposite;
    }
};

// Check if any series belonging to the given yAxis is still visible
const anySeriesInAxisVisible = (chart, yAxisId, yAxis) =>
    chart?.series?.some(s =>
        (s?.options?.yAxis === yAxisId || s?.yAxis === yAxis) &&
        s?.visible &&
        s?.options?.showInLegend !== false
    );

// Merge a config-axis with live visibility/opposite state
const syncAxisFromLiveChart = (liveChart, axis) => {
    const liveAxis = liveChart?.get(axis?.id);
    if (!liveAxis) { return axis; }
    return { ...axis, visible: liveAxis?.visible, opposite: liveAxis?.opposite };
};

// Merge a config-series with live visibility state
const syncSeriesFromLiveChart = (liveChart, s) => {
    const liveSeries = liveChart?.series?.find(cs => cs?.options?.id === s?.id);
    if (!liveSeries) { return s; }
    return { ...s, visible: liveSeries?.visible };
};

// Applies visibility sync, state capture and redraw after a legend click tick
const applyLegendClickAfterTick = (chart, yAxis, yAxisId, chartConfig, setState) => {
    const anySeriesVisible = anySeriesInAxisVisible(chart, yAxisId, yAxis);
    yAxis.update({ visible: anySeriesVisible }, false);
    const visibleYAxis = getVisibleSignalAxes(chart);
    visibleYAxis.forEach(updateOppositeAxis);
    chartConfig.yAxis = chartConfig.yAxis.map(axis => syncAxisFromLiveChart(chart, axis));
    chartConfig.series = chartConfig.series.map(s => syncSeriesFromLiveChart(chart, s));
    const currentSeriesVisibility = {};
    const currentYAxisVisibility = {};
    const currentYAxisOpposite = {};
    chart.series.forEach(series => collectSeriesVisibility(series, currentSeriesVisibility));
    chart.yAxis.forEach(axis => collectYAxisVisibility(axis, currentYAxisVisibility, currentYAxisOpposite));
    setState.setSeriesVisibility(currentSeriesVisibility);
    setState.setYAxisVisibility(currentYAxisVisibility);
    setState.setYAxisOpposite(currentYAxisOpposite);
    chart.redraw();
    setState.setOptions({ ...chartConfig });
};

const RETRAINING_START_PLOTLINE_ID = 'retraining-start-plotline';
const RETRAINING_TRIGGERED_PLOTLINE_ID = 'retraining-triggered-plotline';
const RETRAINING_COMPLETED_PLOTLINE_ID = 'retraining-completed-plotline';

const getMotorChartConfig = (conditionTrendsData, removeKPIData, title, assetDetails) => {
    return {
        title: { text: null },
        chart: {
            events: {
                render: function (chartInstance) {
                    if (this._renderEventDisabled) { return; }
                    afterChartReDrawStringData(chartInstance.target.container.id, removeKPIData);
                },
                beforePrint: function () {
                    if (this.exportSVGElements?.length > 0) {
                        this.exportSVGElements[0]?.hide();
                    }
                    this._renderEventDisabled = true;
                    this.legend?.update({
                        useHTML: false,
                        squareSymbol: true,
                        symbolRadius: 0,
                        symbolHeight: 10,
                        symbolWidth: 10,
                    }, true);
                    const titleDiv = document.createElement('div');
                    titleDiv.style.cssText = 'font-family: Verdana, sans-serif; font-weight: bold; font-size: 14px; padding: 4px 0 4px 50px;';
                    titleDiv.textContent = `${assetDetails?.details?.assetName} - ${title}`;
                    this.container.insertBefore(titleDiv, this.container.firstChild);
                    this._printTitleDiv = titleDiv;

                    // Apply print fonts
                    const legendElement = this.legend?.group?.element;
                    if (this.xAxis?.length > 0) {
                        const xAxisLabels = this.xAxis[0]?.labelGroup?.element;
                        if (xAxisLabels?.style) { xAxisLabels.style.fontFamily = PRINT_FONT_FAMILY; }
                    }
                    this.yAxis?.forEach(axis => {
                        const yAxisLabels = axis.labelGroup?.element;
                        if (yAxisLabels?.style) { yAxisLabels.style.fontFamily = PRINT_FONT_FAMILY; }
                    });
                    if (legendElement?.style) { legendElement.style.fontFamily = PRINT_FONT_FAMILY; }
                },
                afterPrint: function () {
                    if (this.exportSVGElements?.length > 0) {
                        this.exportSVGElements[0]?.show();
                    }
                    if (this._printTitleDiv) {
                        this._printTitleDiv.remove();
                        delete this._printTitleDiv;
                    }
                    this._renderEventDisabled = false;
                    this.legend?.update({
                        useHTML: true,
                        squareSymbol: false,
                        symbolHeight: 8,
                        symbolWidth: 8,
                    }, true);
                }
            },
            style: { fontFamily: PRINT_FONT_FAMILY },
            spacingTop: 10,
            spacingRight: 25,
        },
        boost: { allowForce: false },
        time: { useUTC: false },
        enableNoDataZoom: true,
        navigator: { enabled: true },
        xAxis: {
            type: 'datetime',
            id: 'x',
            dateTimeLabelFormats: {
                millisecond: '%H:%M:%S',
                day: '%e. %b',
                month: '%b \'%y'
            },
            crosshair: { enabled: true, width: 2, color: colors.tundora },
            labels: {
                rotation: 50,
                color: '#000000',
                style: { fontWeight: 'bold', fontColor: '#000000' },
                formatter: function () {
                    return getXAxisLabelDateFormatForDriveAnomaly(this.value);
                }
            },
            lineColor: '#000000',
            lineWidth: 2,
            tickPixelInterval: 25,
            tickColor: '#000000',
            tickWidth: 2,
        },
        legend: {
            useHTML: true,
            layout: 'horizontal',
            align: 'right',
            itemStyle: { fontFamily: PRINT_FONT_FAMILY, fontSize: '14px' },
            itemDistance: 16,
            itemMarginTop: 10,
            symbolRadius: 0,
            squareSymbol: false,
            symbolHeight: 8,
            symbolWidth: 8,
        },
        credits: { enabled: false },
        tooltip: {
            shared: true,
            valueDecimals: 2,
            useHTML: true,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            shadow: false,
            padding: 0,
            borderWidth: 0,
            dateTimeLabelFormats: {
                day: '%d.%m.%Y<br/>%H:%M',
                hour: '%d.%m %H:%M',
            },
            positioner: function (labelWidth, labelHeight, point) {
                const chart = this.chart;
                let x = point.plotX + chart.plotLeft;
                let y = point.plotY + chart.plotTop - labelHeight - 16;
                x = Math.min(x, chart.plotLeft + chart.plotWidth - labelWidth);
                y = Math.max(chart.plotTop, y);
                return { x, y };
            },
            formatter: function () {
                let anomalyLevel;
                if (this.points?.length === 1 && this.points[0]?.series?.name === speedKPI) {
                    anomalyLevel = null;
                } else {
                    anomalyLevel = conditionTrendsData.find(
                        kpi => kpi.type === CHART_CONDITION_TREND_KPI_TYPE
                    )?.values?.find(
                        value => new Date(value.timestamp).getTime() === this.x
                    )?.anomalyLevel;
                }
                return renderToStaticMarkup(
                    <TrendTooltip
                        points={this.points?.filter(
                            point => ![CHART_CONDITION_TREND_KPI_TYPE].includes(point?.series?.name)
                        )}
                        timeStamp={this.x}
                        isUTC={false}
                        anomalyLevel={anomalyLevel}
                    />
                );
            },
        },
        plotOptions: {
            series: {
                turboThreshold: 0,
                animation: false,
                boostThreshold: 1000,
            },
            scatter: { marker: { enabled: true }, lineWidth: 0 },
            column: {
                boostThreshold: 1000,
                groupPadding: 0,
                pointPadding: 0,
                borderWidth: 0,
                pointPlacement: 0.5,
            }
        },
        navigation: {
            buttonOptions: { align: 'right' },
            menuStyle: {
                borderColor: '#fff',
                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
                borderRadius: '6px'
            },
            menuItemStyle: { padding: '6px 12px', fontSize: '14px', fontFamily: PRINT_FONT_FAMILY },
            menuItemHoverStyle: { backgroundColor: '#ebebeb', color: '#1f1f1f' }
        },
        lang: {
            printChart: translate('ABB.Powertrain.Frontend.printChart'),
            downloadPNG: translate('ABB.Powertrain.Frontend.saveAsImage'),
            noData: renderToStaticMarkup(
                <ChartNoDataText
                    title={translate('ABB.Powertrain.Frontend.chartNoMeasurementData')}
                    text={translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide')}
                />
            )
        },
        noData: { useHTML: true },
        exporting: {
            enabled: true,
            scale: 1.5,
            sourceWidth: 1100,
            sourceHeight: 500,
            fallbackToExportServer: false,
            allowHTML: true,
            printMaxWidth: 1100,
            buttons: {
                contextButton: { enabled: false }
            },
            chartOptions: {
                chart: { spacingTop: 70 },
                legend: {
                    useHTML: false,
                    squareSymbol: true,
                    symbolRadius: 0,
                    symbolHeight: 10,
                    symbolWidth: 10,
                },
                title: {
                    text: `${assetDetails?.details?.assetName} - ${title}`,
                    align: 'left',
                    x: 50,
                    y: -10,
                    style: { fontFamily: 'Verdana, sans-serif', fontWeight: 'bold' }
                },
                xAxis: { labels: { style: { fontFamily: 'Verdana, sans-serif' } } },
                yAxis: { labels: { style: { fontFamily: 'Verdana, sans-serif' } } }
            }
        }
    };
};


const getMeasurements = (measurements) => {
    const dataPoints = [];
    forEach(measurements, (measurement) => {
        if (measurement.timestamp !== null) {
            const timestamp = new Date(measurement.timestamp).getTime();
            dataPoints.push({ x: timestamp, y: measurement?.value });
        }
    });
    return dataPoints;
};


const getStatusBarData = (trendValues) => {
    return map(trendValues?.values, ul => ({
        y: 1,
        x: new Date(ul?.timestamp)?.getTime(),
        color: statusBarColors[ul?.value],
    }));
};

// Anomaly yellow marker series builder
const createAnomalySeries = (measurements, conditionTrendsData) => {
    const anomalyPoints = [];
    const anomalyData = conditionTrendsData?.find(item => item.type === CHART_CONDITION_TREND_KPI_TYPE);
    const anomalyTimestamps = {};
    if (anomalyData?.values) {
        forEach(anomalyData.values, (value) => {
            if (value.timestamp) {
                anomalyTimestamps[new Date(value.timestamp).getTime()] = value.anomalyLevel;
            }
        });
    }
    forEach(measurements, (measurement) => {
        if (measurement.timestamp !== null) {
            const timestamp = new Date(measurement.timestamp).getTime();
            if (anomalyTimestamps[timestamp] > 0) {
                anomalyPoints.push({ x: timestamp, y: measurement?.value });
            }
        }
    });
    return anomalyPoints;
};


const getMotorSeries = (conditionTrendsData, valueType, chartInstance) => {
    const seriesConfig = { yAxis: [], seriesData: [], allSeriesLength: 0 };
    const coloredTrends = setColor(conditionTrendsData);

    const calculateAnomalyTitleY = (inst) => {
        const totalHeight = inst?.chartHeight || inst?.plotHeight || chartHeightConstants.min;
        const statusBarTopPercent = 91.5;
        const spaceWidth = totalHeight < 1000 ? 20 : 15;
        const statusBarTopPixels = statusBarTopPercent / 100 * totalHeight;
        const statusBarCenterY = -(totalHeight - statusBarTopPixels - spaceWidth);
        return Math.max(-50, Math.min(-5, statusBarCenterY));
    };

    if (coloredTrends?.length) {
        transform(coloredTrends, (acc, item) => {
            if (item.type === CHART_CONDITION_TREND_KPI_TYPE) {
                const anomalyStatusBarYAxisId = 'anomaly_status_bar_yaxis';
                acc.yAxis.push({
                    top: '91.5%',
                    height: '8%',
                    id: anomalyStatusBarYAxisId,
                    title: {
                        text: translate('ABB.Powertrain.Frontend.anomalyOverallStatusBarLabel'),
                        align: 'low',
                        x: 10,
                        y: calculateAnomalyTitleY(chartInstance),
                        style: {
                            fontFamily: PRINT_FONT_FAMILY,
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '12px',
                            whiteSpace: 'nowrap',
                            transform: 'rotate(0deg)'
                        },
                    },
                    labels: { enabled: false },
                    gridLineWidth: 0,
                    lineWidth: 0,
                    tickLength: 0,
                });
                acc.seriesData.push({
                    name: CHART_CONDITION_TREND_KPI_TYPE,
                    type: 'column',
                    id: `${crypto.randomUUID()}`,
                    yAxis: anomalyStatusBarYAxisId,
                    xAxis: 'x',
                    data: getStatusBarData(item),
                    showInLegend: false,
                    enableMouseTracking: false,
                    states: { inactive: { enabled: false }, hover: { enabled: false } }
                });
            } else {
                const unit = item.unit || '';
                const convertedUnit = getMeasurementUnitSymbol(item.unitGroupId);
                const precision = item.precision;
                const yAxisId = `y_${item.key}`;
                const seriesId = item.key;
                const anomalySeriesId = `anomaly_${item.key}`;
                const name = item?.timeseriesName;
                const trendDataValues = getMeasurements(item.values);
                const anomalyPoints = createAnomalySeries(item.values, coloredTrends);
                const unitLabel = convertedUnit || unit;
                const yAxisTitleText = unitLabel ? `${name} (${unitLabel})` : name;

                acc.yAxis.push({
                    top: '0%',
                    height: '82%',
                    labels: { format: '{value}', style: { color: item.color } },
                    id: yAxisId,
                    title: {
                        text: yAxisTitleText,
                        enabled: true,
                        style: { color: item.color }
                    },
                    visible: item.key === defaultOverallVibrationSignal,
                    isSignalYAxis: true
                });
                const valueTypeSuffix = valueType ? ` (${valueType})` : '';
                acc.seriesData.push({
                    name: `${name}${valueTypeSuffix}`,
                    data: trendDataValues,
                    tooltip: { valueSuffix: ` ${convertedUnit || unit}`, valueDecimals: precision },
                    color: item.color,
                    showInNavigator: true,
                    visible: item.key === defaultOverallVibrationSignal,
                    showInLegend: true,
                    marker: { enabled: true, radius: 2, symbol: 'square', lineWidth: 0 },
                    lineWidth: 1,
                    id: seriesId,
                    yAxis: yAxisId,
                    xAxis: 'x',
                    enableMouseTracking: true
                });

                if (anomalyPoints?.length > 0 && name !== speedKPI) {
                    acc.seriesData.push({
                        name: `${name}`,
                        type: 'scatter',
                        data: anomalyPoints,
                        color: colors.orangePeel,
                        marker: {
                            enabled: true,
                            fillColor: colors.orangePeel,
                            radius: 2,
                            symbol: 'square',
                            lineWidth: 0,
                            lineColor: colors.orangePeel,
                        },
                        showInLegend: false,
                        showInNavigator: false,
                        visible: item.key === defaultOverallVibrationSignal,
                        tooltip: { enabled: false },
                        id: anomalySeriesId,
                        yAxis: yAxisId,
                        xAxis: 'x',
                        linkedTo: seriesId,
                        enableMouseTracking: false
                    });
                }
            }
        }, seriesConfig);
    }

    const allSeriesLength = seriesConfig.seriesData?.length
        ? seriesConfig.seriesData.reduce((count, series) => series.data?.length + count, 0)
        : 0;

    return { ...seriesConfig, allSeriesLength };
};

// Retraining plotline factory
//Preserve the exact timestamp to keep retraining plotlines aligned.
const dateToEpoch = (date) => {
    if (!date) { return null; }
    const epoch = new Date(date).getTime();
    return Number.isNaN(epoch) ? null : epoch;
};

const getPlotBounds = (chartInstance) => {
    const plotLeft = chartInstance?.plotLeft ?? 0;
    const plotWidth = chartInstance?.plotWidth ?? 0;
    return { plotLeft, plotWidth, plotRight: plotLeft + plotWidth };
};

const getPixelPosition = (dateEpoch, chartInstance) => {
    const { min, max } = chartInstance?.xAxis?.[0] || {};
    const { plotLeft, plotWidth } = getPlotBounds(chartInstance);
    if (min === null || min === undefined || max === null || max === undefined || max <= min || !plotWidth) {
        return null;
    }
    const position = (dateEpoch - min) / (max - min);
    return plotLeft + position * plotWidth;
};

const isPresent = (value) => value !== null && value !== undefined;

const resolveLabelSide = (side, linePixel, plotLeft, plotWidth) => {
    if (isPresent(side)) { return side; }
    const isNearRightEdge = isPresent(linePixel) && plotWidth > 0 && (linePixel - plotLeft) / plotWidth > 0.7;
    return isNearRightEdge ? Alignment.LEFT : Alignment.RIGHT;
};

const getLeftBoundary = (plotLeft, neighborPixel, linePixel) => {
    if (isPresent(neighborPixel) && neighborPixel <= linePixel) {
        return Math.max(plotLeft, neighborPixel);
    }
    return plotLeft;
};

const getAvailableSpace = (linePixel, side, leftBoundary, plotRight) => {
    if (isPresent(linePixel)) {
        return side === Alignment.LEFT
            ? linePixel - leftBoundary - LABEL_EDGE_MARGIN
            : plotRight - linePixel - LABEL_EDGE_MARGIN;
    }
    return null;
};

const getLabelSide = (dateEpoch, chartInstance, forceSide, neighborEpoch) => {
    const { plotLeft, plotWidth, plotRight } = getPlotBounds(chartInstance);
    const linePixel = getPixelPosition(dateEpoch, chartInstance);
    const neighborPixel = isPresent(neighborEpoch) ? getPixelPosition(neighborEpoch, chartInstance) : null;
    let side = resolveLabelSide(forceSide, linePixel, plotLeft, plotWidth);
    const leftBoundary = getLeftBoundary(plotLeft, neighborPixel, linePixel);
    const leftSpace = getAvailableSpace(linePixel, Alignment.LEFT, leftBoundary, plotRight);
    const rightSpace = getAvailableSpace(linePixel, Alignment.RIGHT, leftBoundary, plotRight);
    let availableSpace = side === Alignment.LEFT ? leftSpace : rightSpace;

    // If there is no room on the right, always move label to the left to avoid right-edge clipping.
    if (side === Alignment.RIGHT && isPresent(rightSpace) && rightSpace < MIN_LABEL_MAX_WIDTH) {
        side = Alignment.LEFT;
        availableSpace = leftSpace;
    } else if (side === Alignment.LEFT && isPresent(leftSpace) && leftSpace < MIN_LABEL_MAX_WIDTH) {
        if (isPresent(rightSpace) && rightSpace > leftSpace) {
            side = Alignment.RIGHT;
            availableSpace = rightSpace;
        }
    }

    const maxWidth = isPresent(availableSpace) ? Math.max(availableSpace, 0) : null;

    if (side === Alignment.LEFT) {
        return { labelAlign: Alignment.RIGHT, labelX: -8, maxWidth };
    }
    return { labelAlign: Alignment.LEFT, labelX: 8, maxWidth };
};

const pickSideWithMoreRoom = (dateEpoch, neighborEpoch, chartInstance) => {
    const { plotLeft, plotRight } = getPlotBounds(chartInstance);
    const linePixel = getPixelPosition(dateEpoch, chartInstance);
    const neighborPixel = getPixelPosition(neighborEpoch, chartInstance);

    if (linePixel === null) { return Alignment.RIGHT; }

    const rightSpace = plotRight - linePixel;
    const leftBoundary = neighborPixel === null ? plotLeft : Math.max(plotLeft, neighborPixel);
    const leftSpace = linePixel - leftBoundary;

    return leftSpace > rightSpace ? Alignment.LEFT : Alignment.RIGHT;
};

const buildPlotline = (dateEpoch, id, labelKey, chartInstance, forceSide, neighborEpoch, labelY = PLOTLINE_DEFAULT_LABEL_Y) => {
    const { labelAlign, labelX, maxWidth } = getLabelSide(dateEpoch, chartInstance, forceSide, neighborEpoch);
    const labelText = translate(`ABB.Powertrain.Frontend.${labelKey}`);
    return {
        id,
        color: 'rgba(0, 0, 0, 0.24)',
        width: 3,
        value: dateEpoch,
        zIndex: 5,
        label: {
            y: labelY,
            x: labelX,
            align: labelAlign,
            rotation: 0,
            useHTML: false,
            style: {
                fontFamily: PRINT_FONT_FAMILY,
                color: '#1F1F1F',
                fontSize: '12px',
                fontWeight: '500',
                fontStyle: 'normal',
                lineHeight: '14px',
                textOverflow: 'clip',
                ...maxWidth ? { width: `${Math.round(maxWidth)}px` } : {}
            },
            text: labelText
        }
    };
};

const getStartAndTriggeredSideConfig = (startEpoch, triggeredEpoch, chartInstance) => {
    const initialConfig = {
        startForceSide: null,
        triggeredForceSide: null,
        startNeighborEpoch: null,
        triggeredNeighborEpoch: null,
    };

    if (isPresent(startEpoch) && isPresent(triggeredEpoch)) {
        const startIsEarlier = startEpoch <= triggeredEpoch;
        const earlierEpoch = startIsEarlier ? startEpoch : triggeredEpoch;
        const laterEpoch = startIsEarlier ? triggeredEpoch : startEpoch;
        const earlierForceSide = Alignment.LEFT;
        const laterForceSide = pickSideWithMoreRoom(laterEpoch, earlierEpoch, chartInstance);

        if (startIsEarlier) {
            return {
                startForceSide: earlierForceSide,
                triggeredForceSide: laterForceSide,
                startNeighborEpoch: null,
                triggeredNeighborEpoch: startEpoch,
            };
        }

        return {
            startForceSide: laterForceSide,
            triggeredForceSide: earlierForceSide,
            startNeighborEpoch: triggeredEpoch,
            triggeredNeighborEpoch: null,
        };
    }

    return initialConfig;
};

const shouldStackRetrainingLabel = (firstEpoch, secondEpoch, chartInstance) => {
    const firstPixel = isPresent(firstEpoch) ? getPixelPosition(firstEpoch, chartInstance) : null;
    const secondPixel = isPresent(secondEpoch) ? getPixelPosition(secondEpoch, chartInstance) : null;
    if (!isPresent(firstPixel) || !isPresent(secondPixel)) { return false; }
    const overlapThreshold = Math.max(CLOSE_LABEL_PIXEL_THRESHOLD, MIN_LABEL_MAX_WIDTH * 2 + LABEL_EDGE_MARGIN);
    return Math.abs(secondPixel - firstPixel) < overlapThreshold;
};

const getCompletedSideConfig = (startEpoch, triggeredEpoch, completedEpoch, chartInstance) => {
    if (!isPresent(completedEpoch)) {
        return { completedNeighborEpoch: null, completedForceSide: null };
    }

    const candidateNeighborEpochs = [startEpoch, triggeredEpoch]
        .filter(epoch => isPresent(epoch) && epoch <= completedEpoch)
        .sort((left, right) => right - left);
    const nearestEarlierEpoch = candidateNeighborEpochs[0];

    if (!isPresent(nearestEarlierEpoch)) {
        return { completedNeighborEpoch: null, completedForceSide: null };
    }

    return {
        completedNeighborEpoch: nearestEarlierEpoch,
        completedForceSide: pickSideWithMoreRoom(completedEpoch, nearestEarlierEpoch, chartInstance),
    };
};

const createRetrainingPlotlines = (retrainingStartDate, requestedSubmittedDate, retrainingCompletedDate, retrainingStatus, chartInstance) => {
    const startEpoch = dateToEpoch(retrainingStartDate);
    const triggeredEpoch = dateToEpoch(requestedSubmittedDate);
    const isRetrainingCompleted = retrainingStatus === RETRAINING_STATUS.COMPLETED;
    const completedEpoch = isRetrainingCompleted ? dateToEpoch(retrainingCompletedDate) : null;

    const {
        startForceSide,
        triggeredForceSide,
        startNeighborEpoch,
        triggeredNeighborEpoch,
    } = getStartAndTriggeredSideConfig(startEpoch, triggeredEpoch, chartInstance);

    const {
        completedNeighborEpoch,
        completedForceSide,
    } = getCompletedSideConfig(startEpoch, triggeredEpoch, completedEpoch, chartInstance);

    const shouldStackTriggeredLabel = shouldStackRetrainingLabel(startEpoch, triggeredEpoch, chartInstance);
    const shouldStackCompletedLabel = [startEpoch, triggeredEpoch]
        .filter(isPresent)
        .some(epoch => shouldStackRetrainingLabel(epoch, completedEpoch, chartInstance));

    const plotlines = [];
    if (isPresent(startEpoch)) {
        plotlines.push(buildPlotline(startEpoch, RETRAINING_START_PLOTLINE_ID, 'retrainingStartingDateLabel', chartInstance, startForceSide, startNeighborEpoch));
    }
    if (isPresent(triggeredEpoch)) {
        plotlines.push(buildPlotline(
            triggeredEpoch,
            RETRAINING_TRIGGERED_PLOTLINE_ID,
            'retrainingTriggered',
            chartInstance,
            triggeredForceSide,
            triggeredNeighborEpoch,
            shouldStackTriggeredLabel ? PLOTLINE_STACKED_LABEL_Y : PLOTLINE_DEFAULT_LABEL_Y
        ));
    }
    if (isPresent(completedEpoch)) {
        plotlines.push(buildPlotline(
            completedEpoch,
            RETRAINING_COMPLETED_PLOTLINE_ID,
            'retrainingCompletedPlotline',
            chartInstance,
            completedForceSide,
            completedNeighborEpoch,
            shouldStackCompletedLabel ? PLOTLINE_STACKED_LABEL_Y : PLOTLINE_DEFAULT_LABEL_Y
        ));
    }
    return plotlines;
};

const useMotorAnomalyChartConfig = (
    anomalyDetectionKPI,
    removeKPIData,
    title,
    assetDetails,
    valueType,
    conditionTrendsData,
    { containerRef, chartInstance, chartHeight, retrainingStartDate, requestedSubmittedDate, retrainingCompletedDate, retrainingStatus }
) => {
    const [options, setOptions] = useState({});
    const [navigatorExtremes, setNavigatorExtremes] = useState(null);
    const [seriesVisibility, setSeriesVisibility] = useState({});
    const [yAxisVisibility, setYAxisVisibility] = useState({});
    const [yAxisOpposite, setYAxisOpposite] = useState({});

    const chartConfig = getMotorChartConfig(
        conditionTrendsData,
        removeKPIData,
        title,
        assetDetails
    );

    const handleSetExtremes = useCallback((e) => {
        if (e.trigger === 'navigator') {
            setNavigatorExtremes({ min: e.min, max: e.max, trigger: e.trigger });
        }
    }, [setNavigatorExtremes]);

    const captureCurrentStates = useCallback(() => {
        if (chartInstance) {
            const currentSeriesVisibility = {};
            const currentYAxisVisibility = {};
            const currentYAxisOpposite = {};
            chartInstance?.series?.forEach(series => {
                if (series.options.showInLegend !== false && series.options.id) {
                    currentSeriesVisibility[series.options.id] = series.visible;
                }
            });
            chartInstance?.yAxis?.forEach(axis => {
                if (axis.options.id) {
                    currentYAxisVisibility[axis.options.id] = axis.visible;
                    currentYAxisOpposite[axis.options.id] = axis.opposite;
                }
            });
            setSeriesVisibility(currentSeriesVisibility);
            setYAxisVisibility(currentYAxisVisibility);
            setYAxisOpposite(currentYAxisOpposite);
        }
    }, [chartInstance]);

    useEffect(() => {
        const containerHeight = containerRef?.current?.clientHeight;
        chartConfig.chart.height = calculateDynamicChartHeight(
            conditionTrendsData,
            false, // isDriveAnomaly = false
            containerHeight || chartHeightConstants.default
        );
    }, []);

    useEffect(() => {
        if (chartInstance?.xAxis?.length > 0) {
            chartInstance.xAxis[0]?.update({
                ...navigatorExtremes && { min: navigatorExtremes?.min, max: navigatorExtremes?.max }
            });
        }
    }, [navigatorExtremes, chartInstance]);

    useEffect(() => {
        if (!chartInstance?.xAxis?.length) { return; }
        const xAxis = chartInstance.xAxis[0];
        if (!xAxis) { return; }
        let lastGeometrySignature = null;
        const refreshRetrainingPlotlines = () => {
            const { min, max } = xAxis || {};
            const geometrySignature = `${chartInstance.plotLeft}|${chartInstance.plotWidth}|${min}|${max}`;
            if (geometrySignature === lastGeometrySignature) { return; }
            lastGeometrySignature = geometrySignature;

            xAxis.removePlotLine(RETRAINING_START_PLOTLINE_ID);
            xAxis.removePlotLine(RETRAINING_TRIGGERED_PLOTLINE_ID);
            xAxis.removePlotLine(RETRAINING_COMPLETED_PLOTLINE_ID);
            const plotlines = createRetrainingPlotlines(retrainingStartDate, requestedSubmittedDate, retrainingCompletedDate, retrainingStatus, chartInstance);
            plotlines.forEach(pl => xAxis.addPlotLine(pl));
        };

        refreshRetrainingPlotlines();
        const removeRedrawListener = Highcharts.addEvent(chartInstance, 'redraw', refreshRetrainingPlotlines);

        return () => {
            removeRedrawListener();
        };
    }, [chartInstance, retrainingStartDate, requestedSubmittedDate, retrainingCompletedDate, retrainingStatus]);

    useEffect(() => {
        if (chartInstance?.series?.length && chartInstance?.yAxis?.length) {
            chartInstance?.series?.forEach(series => {
                if (seriesVisibility[series.options.id] !== undefined) {
                    series.setVisible(seriesVisibility[series.options.id], false);
                }
            });
            chartInstance?.yAxis?.forEach(axis => {
                if (yAxisVisibility[axis.options.id] !== undefined || yAxisOpposite[axis.options.id] !== undefined) {
                    const updateProps = {};
                    if (yAxisVisibility[axis.options.id] !== undefined) { updateProps.visible = yAxisVisibility[axis.options.id]; }
                    if (yAxisOpposite[axis.options.id] !== undefined) { updateProps.opposite = yAxisOpposite[axis.options.id]; }
                    axis.update(updateProps, false);
                }
            });
            chartInstance.redraw();
        }
    }, [chartInstance, seriesVisibility, yAxisVisibility, yAxisOpposite]);

    useEffect(() => {
        captureCurrentStates();

        const { yAxis, seriesData, allSeriesLength } = getMotorSeries(
            conditionTrendsData,
            valueType,
            chartInstance
        );

        const updatedSeriesData = seriesData.map(series => {
            if (seriesVisibility[series.id] !== undefined) {
                return { ...series, visible: seriesVisibility[series.id] };
            }
            return series;
        });

        const updatedYAxis = yAxis.map(axis => {
            const updates = { ...axis };
            if (yAxisVisibility[axis.id] !== undefined) { updates.visible = yAxisVisibility[axis.id]; }
            if (yAxisOpposite[axis.id] !== undefined) { updates.opposite = yAxisOpposite[axis.id]; }
            return updates;
        });

        chartConfig.xAxis = {
            ...chartConfig.xAxis,
            events: { afterSetExtremes: handleSetExtremes }
        };
        chartConfig.yAxis = updatedYAxis;
        chartConfig.series = updatedSeriesData;
        chartConfig.plotOptions.series.boostThreshold =
            allSeriesLength > defaultBoostThreshold ? 1 : 0;

        chartConfig.plotOptions.series.events = {
            legendItemClick: function (e) {
                const chart = this.chart;
                chartLegendItemClick(e);
                const yAxisId = this.options.yAxis;
                const yAxis = chart?.get(yAxisId) || this.yAxis;

                setTimeout(() => {
                    if (yAxis) {
                        applyLegendClickAfterTick(chart, yAxis, yAxisId, chartConfig, {
                            setSeriesVisibility, setYAxisVisibility, setYAxisOpposite, setOptions
                        });
                    }
                }, 0);

                return true;
            }
        };

        setOptions({ ...chartConfig });
    }, [
        anomalyDetectionKPI,
        valueType,
        conditionTrendsData,
        captureCurrentStates,
        chartHeight,
        retrainingStartDate,
        requestedSubmittedDate,
        retrainingCompletedDate,
        retrainingStatus
    ]);

    return options;
};

export default useMotorAnomalyChartConfig;
